import "./btn-split.css"
import cn from "classnames"
import { useTranslation } from "@nova/use-translation"
import React, { useEffect, useRef } from "react"
import * as Icons from "@nova/component-icons"
import { createPortal } from "react-dom"

type DropdownButton = {
    label: string
    onClick: () => void
    className?: string
    disabled?: boolean
}

type ButtonSplitProps = React.HTMLAttributes<HTMLElement> & {
    dropdownButtons: DropdownButton[]
    label: string
    onClick: () => void
    disabled?: boolean
    menuOpen: boolean
    setMenuOpen?: (open: boolean) => void
    canOpenMenuWhenDisabled?: boolean
    bordered?: boolean
    borderColour?: string
    borderRadius?: number
    maxDropdownHeight?: number
    dropdownClassName?: string
    dropdownButtonClassName?: string
    flipMenuDropdown?: boolean
    height?: number
    //use portalId to render the dropdown container in a different area of the DOM, useful for getting it outside of scrollable overflow containers
    portalPositioning?: {
        portalId: string
        isFixedPositioned?: boolean
        relativeOffset: number
    }
}

export const ButtonSplit = ({
    label,
    onClick,
    className,
    dropdownClassName,
    dropdownButtonClassName,
    dropdownButtons,
    disabled,
    menuOpen,
    setMenuOpen,
    canOpenMenuWhenDisabled = true,
    bordered,
    borderColour = "white",
    borderRadius = 9,
    style,
    maxDropdownHeight,
    flipMenuDropdown,
    height = 28,
    portalPositioning,
    ...rest
}: ButtonSplitProps) => {
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null)
    const menuOpenClick = setMenuOpen ? () => setMenuOpen(!menuOpen) : onClick

    const container = containerRef.current
    const b = container?.getBoundingClientRect()
    const y =
        (portalPositioning?.isFixedPositioned ? b?.top : (container?.offsetTop || 0) + 86) || 0
    const x = portalPositioning?.isFixedPositioned ? b?.left : container?.offsetLeft
    const portal = document.getElementById(portalPositioning?.portalId || "")

    useEffect(() => {
        const closeDropdownOnScroll = () => {
            setMenuOpen?.(false)
        }
        if (menuOpen) {
            document.addEventListener("wheel", closeDropdownOnScroll, { once: true })
        }
        return () => {
            document.removeEventListener("wheel", closeDropdownOnScroll)
        }
    }, [menuOpen, setMenuOpen])

    return (
        <div
            ref={containerRef}
            className={cn(
                "button-split--container relative items-center",
                {
                    "button-split--container-disabled": disabled,
                },
                className
            )}
            style={
                {
                    "--button-split-radius": `${borderRadius}px`,
                    "--button-split-border": borderColour,
                    height: `${height}px`,
                    ...style,
                } as React.CSSProperties
            }
            {...rest}
        >
            <button
                className={cn("button button-split", {
                    "button-split--no-divider": !setMenuOpen,
                })}
                onClick={onClick}
                disabled={disabled}
                style={{ height: `${height}px` }}
            >
                {t(label)}
            </button>
            <div
                className={cn("button-split--open-menu pr-1 mb-px", {
                    "button-split--open-menu--no-divider": !setMenuOpen,
                })}
                style={{ height: `${height}px` }}
            >
                <Icons.ArrowDropDownIcon
                    dropdownOpen={menuOpen}
                    setDropdownOpen={menuOpenClick}
                    disabled={disabled && !canOpenMenuWhenDisabled}
                />
            </div>
            {portal &&
                createPortal(
                    <DropdownComponent
                        menuOpen={menuOpen}
                        flipMenuDropdown={flipMenuDropdown}
                        dropdownButtons={dropdownButtons}
                        dropdownButtonClassName={dropdownButtonClassName}
                        dropdownClassName={dropdownClassName}
                        maxDropdownHeight={maxDropdownHeight}
                        height={height}
                        bordered={bordered}
                        menuOpenClick={menuOpenClick}
                        style={{
                            top: y + (portalPositioning?.relativeOffset || 0),
                            left: x,
                            position: portalPositioning?.isFixedPositioned ? "fixed" : "absolute",
                            width: "135px",
                        }}
                    />,
                    portal as HTMLElement
                )}
            {!portalPositioning && (
                <DropdownComponent
                    menuOpen={menuOpen}
                    flipMenuDropdown={flipMenuDropdown}
                    dropdownButtons={dropdownButtons}
                    dropdownButtonClassName={dropdownButtonClassName}
                    dropdownClassName={dropdownClassName}
                    maxDropdownHeight={maxDropdownHeight}
                    height={height}
                    bordered={bordered}
                    menuOpenClick={menuOpenClick}
                />
            )}
        </div>
    )
}

const DropdownComponent = ({
    menuOpen,
    flipMenuDropdown,
    dropdownClassName,
    maxDropdownHeight,
    height,
    dropdownButtons,
    dropdownButtonClassName,
    bordered,
    style,
    menuOpenClick,
}: Omit<ButtonSplitProps, "label" | "onClick"> & { menuOpenClick: () => void }) => {
    const { t } = useTranslation()
    return (
        <div
            className={cn(
                "button-split--menu",
                {
                    visible: menuOpen,
                    hidden: !menuOpen,
                    "button-split--menu--flipped": flipMenuDropdown,
                },
                dropdownClassName
            )}
            style={{
                maxHeight: maxDropdownHeight,
                top: `${height}px`,
                boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                ...style,
            }}
        >
            {dropdownButtons.map(
                ({ label, onClick: dropdownOnClick, disabled: buttonDisabled, className }, idx) => {
                    return (
                        <button
                            key={idx}
                            className={cn(
                                "button-split--dropdown-button ",
                                {
                                    "button-split--dropdown-button-top": idx === 0,
                                    "button-split--dropdown-button-bottom":
                                        idx === dropdownButtons.length - 1,
                                    "button-split--dropdown-button-solo":
                                        dropdownButtons.length === 1,
                                    "button-split--dropdown-bordered": bordered,
                                },
                                dropdownButtonClassName,
                                className
                            )}
                            disabled={buttonDisabled}
                            onClick={() => {
                                dropdownOnClick()
                                menuOpenClick()
                            }}
                        >
                            {t(label)}
                        </button>
                    )
                }
            )}
        </div>
    )
}
